import { ChangeEvent } from "react";
import { User, Building2, Mail, Layers, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

type SignUpFormData = {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  merchantType: "rentals" | "development";
  acceptedTerms: boolean;
};

type AuthFormProps = {
  mode: "signup" | "login";
  formData: SignUpFormData;
  isSubmitting: boolean;
  isFormValid: boolean;
  onInputChange: (field: keyof SignUpFormData) => (e: ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onTermsChange: (checked: boolean) => void;
  onSubmit: () => void;
  onModeToggle: () => void;
};

const AuthForm = ({
  mode,
  formData,
  isSubmitting,
  isFormValid,
  onInputChange,
  onSelectChange,
  onTermsChange,
  onSubmit,
  onModeToggle,
}: AuthFormProps): JSX.Element => {
  return (
    <div className="flex w-full flex-1 flex-col justify-center rounded-2xl bg-white p-6 shadow-xl md:p-10">
      <div className="flex flex-col gap-6">
        {/* Form header */}
        <div className="mb-2 text-center">
          <h2 className="mb-2 text-2xl font-bold">
            {mode === "signup" ? "Configura tu Agente IA" : "Inicia Sesión"}
          </h2>
          <p className="text-sm text-gray-600">
            {mode === "signup" ? "Comienza gratis • Sin tarjeta de crédito" : "Accede a tu cuenta"}
          </p>
        </div>

        {mode === "signup" ? (
          <>
            {/* Form fields - First name and Last name in same row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label className="text-sm font-semibold text-gray-700">
                  Nombre <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={formData.firstName}
                    onChange={onInputChange("firstName")}
                    placeholder="Juan"
                    className="bg-gray-50 pl-10 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-sm font-semibold text-gray-700">
                  Apellido <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={formData.lastName}
                    onChange={onInputChange("lastName")}
                    placeholder="Pérez"
                    className="bg-gray-50 pl-10 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Email corporativo <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={onInputChange("email")}
                  placeholder="juan@empresa.com"
                  className="bg-gray-50 pl-10 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Nombre de empresa <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={formData.companyName}
                  onChange={onInputChange("companyName")}
                  placeholder="Inmobiliaria ABC S.A.S"
                  className="bg-gray-50 pl-10 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Tipo de empresa <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Layers className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <select
                  value={formData.merchantType}
                  onChange={onSelectChange}
                  className="w-full rounded-md border border-gray-200 bg-gray-50 py-2 pl-10 pr-3 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                >
                  <option value="rentals">Inmobiliaria Arriendos y/o Venta de Usados</option>
                  <option value="development">Comercializadora / Desarrolladora de Proyectos (Vivienda Nueva)</option>
                </select>
              </div>
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-2">
              <Checkbox
                checked={formData.acceptedTerms}
                onCheckedChange={(checked) => onTermsChange(checked === true)}
                className="mt-0.5"
              />
              <label className="text-sm text-gray-600">
                Acepto los{" "}
                <a href="#" className="text-purple-600 underline">
                  términos y condiciones
                </a>
                {" "}y la{" "}
                <a href="#" className="text-purple-600 underline">
                  política de privacidad
                </a>
              </label>
            </div>
          </>
        ) : (
          <>
            {/* Login mode - only email field */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Email corporativo <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={onInputChange("email")}
                  placeholder="juan@empresa.com"
                  className="bg-gray-50 pl-10 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
                />
              </div>
            </div>
          </>
        )}

        {/* Submit button */}
        <Button
          onClick={onSubmit}
          className="w-full bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-400"
          size="lg"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <CheckCircle2 className="size-4" />
          )}
          {mode === "signup" ? "Crear mi Agente IA" : "Enviar enlace de acceso"}
        </Button>

        {/* Sign in/up link */}
        <p className="text-center text-sm text-gray-600">
          {mode === "signup" ? (
            <>
              ¿Ya tienes cuenta?{" "}
              <button
                className="cursor-pointer font-semibold text-purple-600 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  onModeToggle();
                }}
              >
                Inicia sesión
              </button>
            </>
          ) : (
            <>
              ¿No tienes cuenta?{" "}
              <button
                className="cursor-pointer font-semibold text-purple-600 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  onModeToggle();
                }}
              >
                Regístrate
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthForm;
